import React, { useContext, useState, useCallback, useEffect } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";

import { AuthContext } from "../../context/Auth/AuthContext";
import useSocketListener from "../../hooks/useSocketListener";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

/**
 * Valida contatos contra o WhatsApp de verdade, usando uma conexão normal
 * (Baileys) — a API Oficial da Meta não tem como checar isso antes de
 * enviar. Dois modos de uso, mutuamente exclusivos:
 *
 * - Modo lista: passe `contactListId` (uma Lista de Contatos inteira —
 *   usado pela Campanha de Template Meta).
 * - Modo avulso: passe `contactIds` (array de Contact.id) + `sessionKey`
 *   (identifica essa sessão de validação no canal de socket, ex: o id da
 *   tela/registro sendo editado) — usado pelas telas de Agendamento e
 *   Contatos, que não têm o conceito de Lista de Contatos.
 *
 * `onValidated(result)` é chamado quando a validação termina, com
 * { validCount, invalidCount, invalidItems }.
 */
const ValidateContactListModal = ({ open, onClose, contactListId, contactIds, sessionKey, whatsapps, onValidated }) => {
  const { user, socket } = useContext(AuthContext);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [validating, setValidating] = useState(false);
  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const [result, setResult] = useState(null);

  const isBulkMode = !contactListId;
  const socketEventName = isBulkMode
    ? `ContactValidation-${sessionKey}`
    : `ContactListValidation-${contactListId}`;

  const baileysWhatsapps = (whatsapps || []).filter(
    (w) => w.channel === "whatsapp" && w.status === "CONNECTED"
  );

  useEffect(() => {
    if (open) {
      setValidating(false);
      setProgress({ processed: 0, total: 0 });
      setResult(null);
      setSelectedWhatsappId(baileysWhatsapps[0]?.id || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleEvent = useCallback(
    (data) => {
      if (data.action === "start") {
        setProgress({ processed: 0, total: data.total });
      }
      if (data.action === "progress") {
        setProgress({ processed: data.processed, total: data.total });
      }
      if (data.action === "error") {
        setValidating(false);
        toast.error(data.message || "Falha ao validar os contatos");
      }
      if (data.action === "done") {
        setValidating(false);
        setResult(data);
        if (typeof onValidated === "function") {
          onValidated(data);
        }
      }
    },
    [onValidated]
  );

  useSocketListener(
    socket,
    user,
    socketEventName,
    handleEvent,
    [socketEventName, handleEvent]
  );

  const handleStart = async () => {
    if (!selectedWhatsappId) {
      toast.warning("Selecione uma conexão para validar os contatos.");
      return;
    }
    setValidating(true);
    setResult(null);
    setProgress({ processed: 0, total: 0 });
    try {
      if (isBulkMode) {
        await api.post("/contacts/validate-whatsapp", {
          contactIds,
          whatsappId: selectedWhatsappId,
          sessionKey,
        });
      } else {
        await api.post("/contact-list-items/validate-whatsapp", {
          contactListId,
          whatsappId: selectedWhatsappId,
        });
      }
    } catch (err) {
      setValidating(false);
      toastError(err);
    }
  };

  const percentage = progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;

  return (
    <Dialog open={open} onClose={validating ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Validar contatos no WhatsApp</DialogTitle>
      <DialogContent>
        {!validating && !result && (
          <>
            <Typography variant="body2" style={{ marginBottom: 16, color: "#888" }}>
              A API Oficial não permite verificar previamente se um número tem WhatsApp.
              Selecione uma conexão normal (já conectada) para fazer essa checagem antes de gerar o template.
            </Typography>
            <FormControl variant="outlined" fullWidth margin="dense">
              <InputLabel id="validate-whatsapp-selection-label">Conexão para validar</InputLabel>
              <Select
                labelId="validate-whatsapp-selection-label"
                label="Conexão para validar"
                value={selectedWhatsappId}
                onChange={(e) => setSelectedWhatsappId(e.target.value)}
              >
                {baileysWhatsapps.length === 0 && (
                  <MenuItem value="" disabled>
                    Nenhuma conexão normal conectada
                  </MenuItem>
                )}
                {baileysWhatsapps.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {validating && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <Box position="relative" display="inline-flex" marginBottom={2}>
              <CircularProgress variant="determinate" value={percentage} size={80} thickness={4} />
              <Box
                top={0}
                left={0}
                bottom={0}
                right={0}
                position="absolute"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography variant="caption" component="div" color="textSecondary" style={{ fontSize: 14, fontWeight: "bold" }}>
                  {`${percentage}%`}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body1">
              Validando {progress.processed} de {progress.total || "..."} contatos
            </Typography>
          </div>
        )}

        {result && (
          <>
            <Typography variant="body1" style={{ marginBottom: 8 }}>
              <b>{result.validCount}</b> contatos válidos, <b>{result.invalidCount}</b> inválidos (sem WhatsApp).
            </Typography>
            {result.invalidItems?.length > 0 && (
              <>
                <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 4 }}>
                  Contatos removidos (sem WhatsApp):
                </Typography>
                <List dense style={{ maxHeight: 240, overflowY: "auto", border: "1px solid #eee", borderRadius: 4 }}>
                  {result.invalidItems.map((item) => (
                    <ListItem key={item.id} divider>
                      <ListItemText primary={item.name} secondary={item.number} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!validating && !result && (
          <>
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              onClick={handleStart}
              color="primary"
              variant="contained"
              disabled={!selectedWhatsappId}
            >
              Iniciar validação
            </Button>
          </>
        )}
        {validating && <Button disabled>Validando...</Button>}
        {result && (
          <Button onClick={onClose} color="primary" variant="contained">
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ValidateContactListModal;
